import React, { useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks';
import { getApplicationSettings } from '@/common/services';
import { CenteredSpinner } from '@/common/components';
import { logError } from '@/common/services/monitor';
import { getAppConfigurations } from '@/common/utils/environment-variables';
import { DEFAULT_PURPOSE_MIN_LENGTH } from '@/constants';

interface ApplicationState {
  appSettings: API.PublicApplicationSettingsDto;
  loading: boolean;
}

const DEFAULT_SETTINGS: API.PublicApplicationSettingsDto = {
  suggestionMonthDiff: 6,
  isUserFeedbackFeatureEnabled: false,
  isFeedbackFeatureEnabled: false,
};

const isValidMinimum = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0;

export const ApplicationContext = React.createContext<ApplicationState>({
  appSettings: DEFAULT_SETTINGS,
  loading: false,
});

const ApplicationProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data, isLoading, isError, isSuccess } = useQuery({
    queryFn: () => {
      return getApplicationSettings();
    },
    enabled: !!user,
  });

  const minimum = data?.customerBookingPurposeMinLength;
  const fallbackReason = isError
    ? 'request_failed'
    : isSuccess && (minimum === undefined || minimum === null)
      ? 'missing'
      : isSuccess && !isValidMinimum(minimum)
        ? 'invalid_value'
        : undefined;
  const fallbackReported = useRef(false);

  // The customer is never blocked by missing settings; the fallback is reported instead.
  useEffect(() => {
    if (!fallbackReason || fallbackReported.current) {
      return;
    }
    fallbackReported.current = true;
    logError(
      `customerBookingPurposeMinLength unavailable, falling back to ${DEFAULT_PURPOSE_MIN_LENGTH}`,
      {
        reason: fallbackReason,
        url: `${getAppConfigurations().mainApiEndpoint}/serverInfo/application-settings/public`,
        fallback: DEFAULT_PURPOSE_MIN_LENGTH,
      }
    );
  }, [fallbackReason]);

  if (isLoading) {
    return (
      <div style={{ height: '100vh', background: '#fff' }}>
        <CenteredSpinner message={t('share.navigating')} />
      </div>
    );
  }

  return (
    <ApplicationContext.Provider
      value={{
        appSettings: {
          ...DEFAULT_SETTINGS,
          ...data,
          customerBookingPurposeMinLength: isValidMinimum(minimum) ? minimum : DEFAULT_PURPOSE_MIN_LENGTH,
        },
        loading: isLoading,
      }}
    >
      {children}
    </ApplicationContext.Provider>
  );
};

export default ApplicationProvider;
